import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, startOfWeek, addDays, isSameDay, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, 
  ArrowLeft, Loader2, Clock, MapPin, User, Phone, Home, Trash2
} from "lucide-react";
import { API } from "@/App";

const DiaryPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [dayOffset, setDayOffset] = useState(0);
  const [viewMode, setViewMode] = useState("day"); // "day" or "week"
  const [diaryView, setDiaryView] = useState("me");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedStaff, setSelectedStaff] = useState(null);
  
  // Modal state
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState({
    title: "",
    appointment_type: "viewing",
    date: format(new Date(), "yyyy-MM-dd"),
    time: "10:00",
    duration: 30,
    location: "",
    notes: "",
    contact_name: "",
    contact_phone: "",
    property_address: "",
    assigned_to: "",  // User ID of who the appointment is for
    department: "sales"  // sales or lettings
  });

  const currentDate = addDays(new Date(), dayOffset);
  const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  
  // Calculate week offset for API call (number of weeks from current week)
  const weekOffset = Math.floor(dayOffset / 7);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch staff list
      const staffRes = await axios.get(`${API}/diary/staff`, { headers }).catch(() => ({ data: { staff: [] } }));
      setStaffList(staffRes.data?.staff || []);
      
      // Fetch appointments
      const userFilter = diaryView === "me" ? "me" : (selectedStaff || "");
      const deptFilter = selectedDepartment !== "all" ? selectedDepartment : "";
      
      const apptRes = await axios.get(
        `${API}/diary/appointments?week_offset=${weekOffset}&user=${userFilter}&department=${deptFilter}`,
        { headers }
      ).catch(() => ({ data: { appointments: [] } }));
      
      setAppointments(apptRes.data?.appointments || []);
    } catch (error) {
      console.error("Error fetching diary data:", error);
    } finally {
      setLoading(false);
    }
  }, [token, weekOffset, diaryView, selectedStaff, selectedDepartment]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveAppointment = async () => {
    if (!appointmentForm.title || !appointmentForm.date || !appointmentForm.time) {
      toast.error("Please fill in title, date and time");
      return;
    }
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...appointmentForm,
        start_time: `${appointmentForm.date}T${appointmentForm.time}:00`
      };
      
      if (editingAppointment) {
        await axios.patch(`${API}/diary/appointments/${editingAppointment.appointment_id}`, payload, { headers });
        toast.success("Appointment updated");
      } else {
        await axios.post(`${API}/diary/appointments`, payload, { headers });
        toast.success("Appointment created");
      }
      
      setShowAppointmentModal(false);
      setEditingAppointment(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save appointment");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/diary/appointments/${appointmentId}`, { headers });
      toast.success("Appointment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete appointment");
    }
  };

  const resetForm = () => {
    setAppointmentForm({
      title: "",
      appointment_type: "viewing",
      date: format(new Date(), "yyyy-MM-dd"),
      time: "10:00",
      duration: 30,
      location: "",
      notes: "",
      contact_name: "",
      contact_phone: "",
      property_address: "",
      assigned_to: currentUser.user_id || "",
      department: "sales"
    });
  };

  const openEditModal = (appt) => {
    setEditingAppointment(appt);
    const apptDate = appt.start_time ? parseISO(appt.start_time) : new Date();
    setAppointmentForm({
      title: appt.title || "",
      appointment_type: appt.appointment_type || "viewing",
      date: format(apptDate, "yyyy-MM-dd"),
      time: format(apptDate, "HH:mm"),
      duration: appt.duration || 30,
      location: appt.location || "",
      notes: appt.notes || "",
      contact_name: appt.contact_name || "",
      contact_phone: appt.contact_phone || "",
      property_address: appt.property_address || "",
      assigned_to: appt.user_id || appt.assigned_to || currentUser.user_id || "",
      department: appt.department || "sales"
    });
    setShowAppointmentModal(true);
  };

  const getAppointmentColor = (type, department) => {
    // Sales appointments
    if (department === "sales" || !department) {
      const salesColors = {
        viewing: "bg-blue-500 border-blue-600 text-white",           // Blue
        valuation: "bg-green-500 border-green-600 text-white",       // Green
        meeting: "bg-orange-500 border-orange-600 text-white",       // Orange
        call: "bg-slate-400 border-slate-500 text-white",
        inspection: "bg-red-400 border-red-500 text-white",
        other: "bg-slate-300 border-slate-400 text-slate-800"
      };
      return salesColors[type] || salesColors.other;
    }
    
    // Lettings appointments
    if (department === "lettings") {
      const lettingsColors = {
        viewing: "bg-purple-500 border-purple-600 text-white",       // Purple
        valuation: "bg-yellow-400 border-yellow-500 text-black",     // Yellow
        meeting: "bg-orange-500 border-orange-600 text-white",       // Orange (same)
        call: "bg-slate-400 border-slate-500 text-white",
        inspection: "bg-red-400 border-red-500 text-white",
        other: "bg-slate-300 border-slate-400 text-slate-800"
      };
      return lettingsColors[type] || lettingsColors.other;
    }
    
    // Default fallback
    return "bg-slate-300 border-slate-400 text-slate-800";
  };

  // Generate week days
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  
  // Generate time slots (8am to 8pm)
  const timeSlots = Array.from({ length: 13 }, (_, i) => 8 + i);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-14 z-40">
        <div className="max-w-full mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-8 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-6 h-6 text-blue-600" />
                <h1 className="text-xl font-bold text-slate-800">Diary</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* View Toggle */}
              <Tabs value={diaryView} onValueChange={(v) => { setDiaryView(v); setSelectedDepartment("all"); setSelectedStaff(null); }}>
                <TabsList>
                  <TabsTrigger value="me">My Diary</TabsTrigger>
                  <TabsTrigger value="office">Office Diary</TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* Department Filter (Office view only) */}
              {diaryView === "office" && (
                <>
                  <Tabs value={selectedDepartment} onValueChange={setSelectedDepartment}>
                    <TabsList>
                      <TabsTrigger value="all">All</TabsTrigger>
                      <TabsTrigger value="sales">Sales</TabsTrigger>
                      <TabsTrigger value="lettings">Lettings</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  
                  {staffList.length > 0 && (
                    <Select value={selectedStaff || "all"} onValueChange={(v) => setSelectedStaff(v === "all" ? null : v)}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All staff" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Staff</SelectItem>
                        {staffList
                          .filter(s => {
                            if (selectedDepartment === "all") return true;
                            if (selectedDepartment === "sales") return s.role?.includes("sales") || s.title?.toLowerCase().includes("sales");
                            if (selectedDepartment === "lettings") return s.role?.includes("lettings");
                            return true;
                          })
                          .map((staff) => (
                            <SelectItem key={staff.user_id} value={staff.user_id}>
                              {staff.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                </>
              )}
              
              <Button onClick={() => { setEditingAppointment(null); resetForm(); setShowAppointmentModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Appointment
              </Button>
            </div>
          </div>
          
          {/* Color Legend */}
          <div className="flex flex-wrap items-center gap-4 px-4 py-2 border-t bg-slate-50 text-xs">
            <span className="font-medium text-slate-600">Sales:</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span>Viewing</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-500"></div>
              <span>Valuation</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-orange-500"></div>
              <span>Meeting</span>
            </div>
            <span className="mx-2 text-slate-300">|</span>
            <span className="font-medium text-slate-600">Lettings:</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-purple-500"></div>
              <span>Viewing</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-yellow-400"></div>
              <span>Valuation</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-orange-500"></div>
              <span>Meeting</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        <Card>
          <CardHeader className="pb-2">
            {/* View Mode Toggle & Navigation */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setDayOffset(dayOffset - (viewMode === "day" ? 1 : 7))}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {viewMode === "day" ? "Previous Day" : "Previous Week"}
                </Button>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-lg font-semibold">
                  {viewMode === "day" 
                    ? format(currentDate, "EEEE, d MMMM yyyy")
                    : `${format(currentWeekStart, "d MMMM")} - ${format(addDays(currentWeekStart, 6), "d MMMM yyyy")}`
                  }
                </div>
                <div className="flex border rounded-lg overflow-hidden">
                  <Button 
                    variant={viewMode === "day" ? "default" : "ghost"} 
                    size="sm"
                    className="rounded-none"
                    onClick={() => setViewMode("day")}
                  >
                    Day
                  </Button>
                  <Button 
                    variant={viewMode === "week" ? "default" : "ghost"} 
                    size="sm"
                    className="rounded-none"
                    onClick={() => setViewMode("week")}
                  >
                    Week
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setDayOffset(0)}
                  disabled={dayOffset === 0}
                >
                  Today
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDayOffset(dayOffset + (viewMode === "day" ? 1 : 7))}>
                  {viewMode === "day" ? "Next Day" : "Next Week"}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {/* Daily View */}
            {viewMode === "day" && (
              <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 220px)" }}>
                <div className="min-w-[600px]">
                  {/* Time Slots for Daily View */}
                  {timeSlots.map((hour) => {
                    const hourAppointments = appointments.filter(appt => {
                      if (!appt.start_time) return false;
                      const apptDate = parseISO(appt.start_time);
                      const apptHour = apptDate.getHours();
                      return isSameDay(apptDate, currentDate) && apptHour === hour;
                    });

                    return (
                      <div key={hour} className="flex border-b min-h-[80px]">
                        {/* Time Label */}
                        <div className="w-20 p-3 text-sm text-slate-500 border-r bg-slate-50 flex-shrink-0">
                          {hour.toString().padStart(2, "0")}:00
                        </div>
                        
                        {/* Appointments */}
                        <div className="flex-1 p-2">
                          {hourAppointments.length === 0 ? (
                            <div className="h-full flex items-center justify-center text-slate-300 text-sm">
                              {/* Empty slot */}
                            </div>
                          ) : (
                            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(hourAppointments.length, 3)}, 1fr)` }}>
                              {hourAppointments.map((appt, apptIndex) => (
                                <div
                                  key={appt.appointment_id || apptIndex}
                                  className={`p-3 rounded-lg cursor-pointer border-l-4 shadow-sm ${getAppointmentColor(appt.appointment_type, appt.department)}`}
                                  onClick={() => openEditModal(appt)}
                                >
                                  <div className="font-semibold text-sm">{appt.title}</div>
                                  <div className="flex items-center gap-2 mt-2 text-xs opacity-90">
                                    <Clock className="w-3 h-3" />
                                    {appt.start_time ? format(parseISO(appt.start_time), "HH:mm") : ""}
                                    {appt.duration && ` (${appt.duration} min)`}
                                  </div>
                                  {appt.location && (
                                    <div className="flex items-center gap-2 mt-1 text-xs opacity-90">
                                      <MapPin className="w-3 h-3" />
                                      {appt.location}
                                    </div>
                                  )}
                                  {appt.property_address && (
                                    <div className="flex items-center gap-2 mt-1 text-xs opacity-90">
                                      <Home className="w-3 h-3" />
                                      {appt.property_address}
                                    </div>
                                  )}
                                  {appt.contact_name && (
                                    <div className="flex items-center gap-2 mt-1 text-xs opacity-90">
                                      <User className="w-3 h-3" />
                                      {appt.contact_name}
                                    </div>
                                  )}
                                  <Badge className="mt-2 text-xs" variant="secondary">
                                    {appt.appointment_type} • {appt.department}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Weekly View */}
            {viewMode === "week" && (
              <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 220px)" }}>
                <div className="min-w-[1200px]">
                  {/* Day Headers */}
                  <div className="grid grid-cols-8 border-b bg-slate-50 sticky top-0 z-10">
                    <div className="p-3 text-center text-sm font-medium text-slate-500 border-r">
                      Time
                    </div>
                    {weekDays.map((day, i) => (
                      <div 
                        key={i} 
                        className={`p-3 text-center border-r cursor-pointer hover:bg-blue-50 ${isSameDay(day, new Date()) ? 'bg-blue-50' : ''}`}
                        onClick={() => { setDayOffset(Math.floor((day - new Date()) / (1000 * 60 * 60 * 24))); setViewMode("day"); }}
                      >
                        <div className="text-xs text-slate-500">{format(day, "EEE")}</div>
                        <div className={`text-lg font-semibold ${isSameDay(day, new Date()) ? 'text-blue-600' : ''}`}>
                          {format(day, "d")}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Time Slots */}
                  {timeSlots.map((hour) => (
                    <div key={hour} className="grid grid-cols-8 border-b min-h-[80px]">
                      {/* Time Label */}
                      <div className="p-2 text-xs text-slate-500 border-r bg-slate-50 text-center">
                        {hour.toString().padStart(2, "0")}:00
                      </div>
                      
                      {/* Day Cells */}
                      {weekDays.map((day, dayIndex) => {
                        const dayAppointments = appointments.filter(appt => {
                          if (!appt.start_time) return false;
                          const apptDate = parseISO(appt.start_time);
                          const apptHour = apptDate.getHours();
                          return isSameDay(apptDate, day) && apptHour === hour;
                        });
                        
                        return (
                          <div 
                            key={dayIndex} 
                            className={`p-1 border-r relative ${isSameDay(day, new Date()) ? 'bg-blue-50/30' : ''}`}
                          >
                            {dayAppointments.map((appt, apptIndex) => (
                              <div
                                key={appt.appointment_id || apptIndex}
                                className={`p-2 rounded text-xs mb-1 cursor-pointer border shadow-sm ${getAppointmentColor(appt.appointment_type, appt.department)}`}
                                onClick={() => openEditModal(appt)}
                              >
                                <div className="font-semibold truncate">{appt.title}</div>
                                <div className="flex items-center gap-1 mt-1 opacity-90">
                                  <Clock className="w-3 h-3" />
                                  {appt.start_time ? format(parseISO(appt.start_time), "HH:mm") : ""}
                                </div>
                                {appt.location && (
                                  <div className="flex items-center gap-1 mt-1 opacity-90 truncate">
                                    <MapPin className="w-3 h-3" />
                                    {appt.location}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Appointment Modal */}
      <Dialog open={showAppointmentModal} onOpenChange={setShowAppointmentModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAppointment ? "Edit Appointment" : "New Appointment"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Assign To and Department */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To *</Label>
                <Select 
                  value={appointmentForm.assigned_to} 
                  onValueChange={(v) => setAppointmentForm({ ...appointmentForm, assigned_to: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.user_id} value={staff.user_id}>
                        {staff.name} ({staff.role?.replace(/_/g, ' ')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department</Label>
                <Select 
                  value={appointmentForm.department} 
                  onValueChange={(v) => setAppointmentForm({ ...appointmentForm, department: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="lettings">Lettings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={appointmentForm.title}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, title: e.target.value })}
                placeholder="e.g. Viewing at 123 High Street"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select 
                  value={appointmentForm.appointment_type} 
                  onValueChange={(v) => setAppointmentForm({ ...appointmentForm, appointment_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewing">Viewing</SelectItem>
                    <SelectItem value="valuation">Valuation</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (mins)</Label>
                <Select 
                  value={appointmentForm.duration.toString()} 
                  onValueChange={(v) => setAppointmentForm({ ...appointmentForm, duration: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 mins</SelectItem>
                    <SelectItem value="30">30 mins</SelectItem>
                    <SelectItem value="45">45 mins</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={appointmentForm.date}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={appointmentForm.time}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, time: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Location / Property Address</Label>
              <Input
                value={appointmentForm.location}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, location: e.target.value })}
                placeholder="e.g. 123 High Street, Bedford"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={appointmentForm.contact_name}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, contact_name: e.target.value })}
                  placeholder="Client name"
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input
                  value={appointmentForm.contact_phone}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, contact_phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="flex justify-between">
            {editingAppointment && (
              <Button 
                variant="destructive" 
                onClick={() => {
                  handleDeleteAppointment(editingAppointment.appointment_id);
                  setShowAppointmentModal(false);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAppointmentModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveAppointment} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingAppointment ? "Update" : "Create"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiaryPage;
